/**
 * Shared item-icon renderer. A catalog `.icon` field (MATERIALS / FURNITURE / …) is either an EMOJI glyph
 * (the historical form) or, for the fish species, a raster ASSET PATH (a leading '/', e.g.
 * `/icons/fish/reef_snapper.webp`). Every inventory/market/codex surface renders icons through here so a
 * path becomes a crisp <img> while an emoji stays plain text — one place, one behaviour.
 *
 * DOM-light + test-friendly: the string builders are pure (unit-tested), and the one-time CSS is injected
 * lazily on first use (guarded for a no-`document` test env).
 */

/** True if a catalog `icon` is a raster asset path (leading '/') rather than an emoji glyph. */
export function isIconPath(icon: string | undefined | null): boolean {
  return typeof icon === 'string' && icon.charCodeAt(0) === 47 /* '/' */;
}

/**
 * Render a catalog icon as inline HTML for an **innerHTML** context (a row, a recipe line, a market card).
 * A path → a lazy, square <img class="item-icon"> that inherits its size from the surrounding font (1.15em);
 * an emoji → the glyph unchanged. Safe to drop in beside text exactly where the emoji used to sit.
 */
export function itemIcon(icon: string | undefined | null, cls = 'item-icon'): string {
  if (isIconPath(icon)) {
    ensureItemIconCss();
    return `<img src="${icon}" class="${cls}" loading="lazy" decoding="async" alt="" />`;
  }
  return icon ?? '';
}

/** Category glyph per raster-icon directory — the text-only stand-in when an <img> cannot render.
 *  Keyed by path prefix so a new icon in an existing category needs no code change. */
const PATH_FALLBACKS: readonly (readonly [string, string])[] = [
  ['/icons/fish/', '🐟'],
  ['/icons/elixirs/', '🧪'],
  ['/icons/charms/', '🧿'],
  ['/icons/ammo/', '●'],
  ['/icons/materials/', '📦'],
  ['/icons/professions/', '⚓'],
  ['/icons/store/', '🪙'],
];

/**
 * Text-only fallback for contexts that CANNOT host an <img> — a `<option>`, or a `textContent` toast/badge.
 * A raster path collapses to its category's neutral glyph (fish/elixir/charm/ammo by directory, 📦 for an
 * unknown path); an emoji is returned unchanged. An explicit `fallback` overrides the category glyph.
 */
export function itemIconText(icon: string | undefined | null, fallback?: string): string {
  if (isIconPath(icon)) {
    if (fallback !== undefined) return fallback;
    for (const [prefix, glyph] of PATH_FALLBACKS) if ((icon as string).startsWith(prefix)) return glyph;
    return '📦';
  }
  return icon ?? '';
}

/** The one-time stylesheet for `.item-icon`. `em`-sized so it scales with whatever row it lands in; the
 *  drop-shadow lifts a dark-edged fish (the Ironscale Grouper is the low-contrast case) off dark surfaces. */
export const ITEM_ICON_CSS =
  `.item-icon{display:inline-block;width:1.15em;height:1.15em;object-fit:contain;vertical-align:-0.22em;` +
  `filter:drop-shadow(0 1px 1.5px rgba(0,0,0,.6));}`;

let cssInjected = false;
/** Inject `ITEM_ICON_CSS` once. No-op without a `document` (unit tests) or if already present. */
export function ensureItemIconCss(): void {
  if (cssInjected || typeof document === 'undefined') return;
  cssInjected = true;
  if (document.getElementById('item-icon-style')) return;
  const st = document.createElement('style');
  st.id = 'item-icon-style';
  st.textContent = ITEM_ICON_CSS;
  document.head.appendChild(st);
}
